import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfile } from '../store/slices/authSlice';
import { authAPI, notificationsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { UserIcon, BellIcon, KeyIcon, LinkIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const Settings = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', phone: '', position: '' });
  const [passwords, setPasswords] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [telegramStatus, setTelegramStatus] = useState({ is_linked: false, telegram_username: '', notifications_enabled: false });
  const [telegramCode, setTelegramCode] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [oneIDConfig, setOneIDConfig] = useState({ client_id: '', client_secret: '', redirect_uri: '', enabled: false });

  useEffect(() => {
    if (user) {
      setProfile({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '', phone: user.phone || '', position: user.position || '' });
    }
    fetchTelegramStatus();
    if (user?.role === 'admin') fetchOneIDConfig();
  }, [user]);

  const fetchTelegramStatus = async () => {
    try {
      const response = await notificationsAPI.telegramStatus();
      setTelegramStatus(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchOneIDConfig = async () => {
    try {
      const response = await authAPI.getOneIDConfig();
      setOneIDConfig(response.data);
    } catch (error) { console.error(error); }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateProfile(profile)).unwrap();
      toast.success('Profil yangilandi!');
    } catch (error) { toast.error('Xatolik yuz berdi'); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) { toast.error('Parollar mos kelmaydi'); return; }
    try {
      await authAPI.changePassword({ old_password: passwords.old_password, new_password: passwords.new_password });
      toast.success('Parol o\'zgartirildi!');
      setPasswords({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) { toast.error(error.response?.data?.error || 'Xatolik yuz berdi'); }
  };

  const handleGenerateTelegramCode = async () => {
    setTelegramLoading(true);
    try {
      const response = await notificationsAPI.telegramGenerateCode();
      setTelegramCode(response.data);
      toast.success('Kod yaratildi!');
    } catch (error) { 
      toast.error('Xatolik yuz berdi'); 
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    try {
      await notificationsAPI.telegramUnlink();
      setTelegramStatus({ is_linked: false, telegram_username: '', notifications_enabled: false });
      setTelegramCode(null);
      toast.success('Telegram uzildi!');
    } catch (error) { toast.error('Xatolik yuz berdi'); }
  };

  const handleToggleTelegram = async () => {
    try {
      const response = await notificationsAPI.telegramToggle();
      setTelegramStatus(prev => ({ ...prev, notifications_enabled: response.data.notifications_enabled }));
      toast.success(response.data.message);
    } catch (error) { toast.error(error.response?.data?.error || 'Xatolik yuz berdi'); }
  };

  const handleTestTelegram = async () => {
    try {
      await notificationsAPI.telegramTest();
      toast.success('Test xabar yuborildi!');
    } catch (error) { toast.error(error.response?.data?.error || 'Xatolik yuz berdi'); }
  };

  const handleOneIDSubmit = async (e) => {
    e.preventDefault();
    try {
      await authAPI.updateOneIDConfig(oneIDConfig);
      toast.success('OneID sozlamalari saqlandi!');
    } catch (error) { toast.error('Xatolik yuz berdi'); }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: UserIcon },
    { id: 'password', label: 'Parol', icon: KeyIcon },
    { id: 'telegram', label: 'Telegram', icon: BellIcon },
    ...(user?.role === 'admin' ? [{ id: 'oneid', label: 'OneID', icon: LinkIcon }] : [])
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Sozlamalar</h1>
        <p className="text-gray-500">Profil va tizim sozlamalari</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-64">
          <div className="card p-2">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left ${activeTab === tab.id ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}>
                <tab.icon className="w-5 h-5" />{tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1">
          {activeTab === 'profile' && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Profil ma'lumotlari</h2>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="input-group"><label className="label">Ism</label><input type="text" className="input" value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} /></div>
                  <div className="input-group"><label className="label">Familiya</label><input type="text" className="input" value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} /></div>
                </div>
                <div className="input-group"><label className="label">Email</label><input type="email" className="input" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} /></div>
                <div className="input-group"><label className="label">Telefon</label><input type="text" className="input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
                <div className="input-group"><label className="label">Lavozim</label><input type="text" className="input" value={profile.position} onChange={(e) => setProfile({ ...profile, position: e.target.value })} /></div>
                <button type="submit" className="btn-primary">Saqlash</button>
              </form>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Parolni o'zgartirish</h2>
              <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
                <div className="input-group"><label className="label">Joriy parol</label><input type="password" className="input" value={passwords.old_password} onChange={(e) => setPasswords({ ...passwords, old_password: e.target.value })} required /></div>
                <div className="input-group"><label className="label">Yangi parol</label><input type="password" className="input" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} required minLength={8} /></div>
                <div className="input-group"><label className="label">Yangi parolni tasdiqlang</label><input type="password" className="input" value={passwords.confirm_password} onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })} required /></div>
                <button type="submit" className="btn-primary">O'zgartirish</button>
              </form>
            </div>
          )}

          {activeTab === 'telegram' && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Telegram xabarnomalar</h2>
              
              {telegramStatus.is_linked ? (
                <div className="space-y-4">
                  {/* Ulangan holat */}
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircleIcon className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-medium text-green-800">Telegram ulangan</p>
                      {telegramStatus.telegram_username && (
                        <p className="text-sm text-green-600">@{telegramStatus.telegram_username}</p>
                      )}
                    </div>
                  </div>

                  {/* Xabarnomalar holati */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Xabarnomalar</p>
                      <p className="text-sm text-gray-500">
                        {telegramStatus.notifications_enabled ? 'Yoqilgan' : 'O\'chirilgan'}
                      </p>
                    </div>
                    <button 
                      onClick={handleToggleTelegram}
                      className={`px-4 py-2 rounded-lg ${telegramStatus.notifications_enabled ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
                    >
                      {telegramStatus.notifications_enabled ? 'O\'chirish' : 'Yoqish'}
                    </button>
                  </div>

                  {/* Test va uzish tugmalari */}
                  <div className="flex gap-3">
                    <button onClick={handleTestTelegram} className="btn-outline">
                      Test xabar yuborish
                    </button>
                    <button onClick={handleUnlinkTelegram} className="btn-secondary text-red-600">
                      Telegram ni uzish
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Ulanmagan holat */}
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <XCircleIcon className="w-8 h-8 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-700">Telegram ulanmagan</p>
                      <p className="text-sm text-gray-500">Xabarnomalar olish uchun Telegram ni ulang</p>
                    </div>
                  </div>

                  {telegramCode ? (
                    <div className="space-y-4">
                      {/* Kod ko'rsatish */}
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-700 mb-2">Quyidagi kodni Telegram botga yuboring:</p>
                        <div className="flex items-center gap-4">
                          <code className="text-2xl font-bold text-blue-800 bg-white px-4 py-2 rounded">
                            {telegramCode.code}
                          </code>
                          <button 
                            onClick={() => navigator.clipboard.writeText(`/start ${telegramCode.code}`)}
                            className="btn-outline btn-sm"
                          >
                            Nusxalash
                          </button>
                        </div>
                      </div>

                      {/* Bot link */}
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">1. Telegram botga o'ting:</p>
                        <a 
                          href={telegramCode.bot_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-blue-600 hover:underline font-medium"
                        >
                          @{telegramCode.bot_username}
                        </a>
                        <p className="text-sm text-gray-600 mt-3">2. Botga quyidagi xabarni yuboring:</p>
                        <code className="block bg-gray-100 px-3 py-2 rounded mt-1 text-sm">
                          /start {telegramCode.code}
                        </code>
                      </div>

                      <button 
                        onClick={fetchTelegramStatus} 
                        className="btn-primary"
                      >
                        Tekshirish
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={handleGenerateTelegramCode}
                      disabled={telegramLoading}
                      className="btn-primary"
                    >
                      {telegramLoading ? 'Yuklanmoqda...' : 'Telegram ni ulash'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'oneid' && user?.role === 'admin' && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">OneID konfiguratsiyasi</h2>
              <form onSubmit={handleOneIDSubmit} className="space-y-4">
                <div className="input-group"><label className="label">Client ID</label><input type="text" className="input" value={oneIDConfig.client_id} onChange={(e) => setOneIDConfig({ ...oneIDConfig, client_id: e.target.value })} /></div>
                <div className="input-group"><label className="label">Client Secret</label><input type="password" className="input" value={oneIDConfig.client_secret} onChange={(e) => setOneIDConfig({ ...oneIDConfig, client_secret: e.target.value })} /></div>
                <div className="input-group"><label className="label">Redirect URI</label><input type="url" className="input" value={oneIDConfig.redirect_uri} onChange={(e) => setOneIDConfig({ ...oneIDConfig, redirect_uri: e.target.value })} /></div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={oneIDConfig.enabled} onChange={(e) => setOneIDConfig({ ...oneIDConfig, enabled: e.target.checked })} className="w-4 h-4" />
                  <span>OneID autentifikatsiyasini yoqish</span>
                </label>
                <button type="submit" className="btn-primary">Saqlash</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
